import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getBulkNeeNorDetailsFromIds } from '../../../api/v2/neenor';
import { hasRowsWithoutInvoiceNumbers } from './utils';

export const useMultipleConsignors = ({
  spreadsheetHandle,
  isMultipleConsignors,
  invoiceNumberIndex,
  consignorDetails,
  setConsignorDetails,
  setIsMultipleConsignorsValid,
  setIsMultipleInvoicesValid,
}) => {
  const [invoiceConsignorMapping, setInvoiceConsignorMapping] = useState({});
  const [isMultipleConsignorsModalOpen, setIsMultipleConsignorsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const invoiceLines = spreadsheetHandle?.current?.excelState?.rows?.map((r) => {
    const invNumber = r[invoiceNumberIndex]?.text;
    const consignorId = invoiceConsignorMapping[invNumber];
    return { invNumber, consignorId };
  });

  const fetchConsignorDetails = async (items) => {
    const consignorIds = Array.from(new Set(items.map((item) => item.Consignorid).filter(Boolean)));

    const data = await queryClient.fetchQuery({
      queryKey: ['getBulkNeeNorDetailsFromIds', consignorIds],
      queryFn: () => getBulkNeeNorDetailsFromIds(consignorIds),
    });

    const detailsObj = {};
    data.forEach((item) => {
      detailsObj[item.id] = {
        ...item,
        addressDetailsComplete: !!item.addressDetailsComplete,
        eori: item.eori,
      };
    });
    setConsignorDetails(detailsObj);

    const newInvoiceConsignorMapping = {};
    items.forEach((item) => {
      if (item.InvoiceNumber && item.Consignorid) {
        newInvoiceConsignorMapping[item.InvoiceNumber] = item.Consignorid;
      }
    });
    setInvoiceConsignorMapping(newInvoiceConsignorMapping);
  };

  const allConsignorsHaveValidAddress = (rows) => {
    return rows?.every((row) => {
      const invoiceNumber = row[invoiceNumberIndex]?.text;
      const consignorId = invoiceConsignorMapping[invoiceNumber];
      if (!consignorId || consignorId.trim() === '') return false;
      return consignorDetails[consignorId]?.addressDetailsComplete;
    });
  };

  const calculateIfMultipleConsignorsValid = (state) => {
    const invoicesValid = !hasRowsWithoutInvoiceNumbers(state?.rows, invoiceNumberIndex);
    const consignorsValid = allConsignorsHaveValidAddress(state?.rows);
    setIsMultipleConsignorsValid(consignorsValid);
    setIsMultipleInvoicesValid(invoicesValid);
  };

  const handleUpdateConsignors = (data) => {
    const newConsignorMappings = { ...invoiceConsignorMapping };
    data.forEach((item) => {
      newConsignorMappings[item.invoiceNumber] = item.neeNoridconsignor;
      setConsignorDetails((prev) => ({
        ...prev,
        [item.neeNoridconsignor]: {
          ...prev[item.neeNoridconsignor],
          eori: item.eori,
          addressDetailsComplete: !!item.addressDetailsComplete,
          companyName: item.companyName,
        },
      }));
    });
    setInvoiceConsignorMapping(newConsignorMappings);
  };

  useEffect(() => {
    if (isMultipleConsignors && Object.keys(consignorDetails).length) {
      calculateIfMultipleConsignorsValid(spreadsheetHandle.current.excelState);
    }
  }, [consignorDetails, isMultipleConsignors, invoiceConsignorMapping]);

  return {
    invoiceConsignorMapping,
    invoiceLines,
    isMultipleConsignorsModalOpen,
    setIsMultipleConsignorsModalOpen,
    fetchConsignorDetails,
    handleUpdateConsignors,
    calculateIfMultipleConsignorsValid,
  };
};
